// CSAT (customer satisfaction) — 3-point Good / Neutral / Bad rating shown on
// a resolved/closed ticket in the customer portal. Once submitted it renders
// a read-only thank-you. `CsatBadge` is the compact read-only view reused on
// the agent ticket detail.
import React, { useState } from 'react';
import axios from 'axios';
import { Smiley, SmileyMeh, SmileySad } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const OPTIONS = [
  { value: 'good', label: 'Good', Icon: Smiley, tone: 'text-emerald-600', ring: 'ring-emerald-300 bg-emerald-50' },
  { value: 'neutral', label: 'Neutral', Icon: SmileyMeh, tone: 'text-amber-600', ring: 'ring-amber-300 bg-amber-50' },
  { value: 'bad', label: 'Bad', Icon: SmileySad, tone: 'text-red-600', ring: 'ring-red-300 bg-red-50' },
];

export function CsatBadge({ csat }) {
  const opt = OPTIONS.find(o => o.value === csat?.rating);
  if (!opt) return null;
  const { Icon } = opt;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm" data-testid="csat-badge">
      <Icon size={18} weight="fill" className={opt.tone} /> <span className="font-medium text-zinc-900">{opt.label}</span>
    </span>
  );
}

export default function CsatWidget({ apiBase, ticketKey, csat, onRated, withCredentials = true }) {
  const [rating, setRating] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (csat?.rating) {
    const opt = OPTIONS.find(o => o.value === csat.rating);
    const Icon = opt?.Icon || Smiley;
    return (
      <div data-testid="csat-submitted">
        <p className="text-sm text-zinc-600 mb-2">Thanks for rating your experience!</p>
        <div className="flex items-center gap-2">
          <Icon size={24} weight="fill" className={opt?.tone} />
          <span className="font-semibold text-zinc-900">{opt?.label}</span>
        </div>
        {csat.comment && <p className="text-sm text-zinc-600 mt-2 italic">"{csat.comment}"</p>}
      </div>
    );
  }

  const submit = async () => {
    if (!rating) return toast.error('Pick a rating');
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${apiBase}/customer/tickets/${ticketKey}/csat`,
        { rating, comment: comment.trim() }, { withCredentials });
      toast.success('Thanks for your feedback!');
      onRated?.(data.csat);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit rating');
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="csat-widget">
      <p className="text-sm font-semibold text-zinc-900 mb-2">How did we do?</p>
      <div className="flex items-center gap-2 mb-3">
        {OPTIONS.map(o => {
          const { Icon } = o;
          const active = rating === o.value;
          return (
            <button key={o.value} type="button" onClick={() => setRating(o.value)}
              className={`flex flex-col items-center gap-1 px-5 py-2.5 rounded-lg ring-1 transition-all ${active ? o.ring : 'ring-zinc-200 hover:ring-zinc-300'}`}
              data-testid={`csat-option-${o.value}`}>
              <Icon size={28} weight={active ? 'fill' : 'regular'} className={active ? o.tone : 'text-zinc-400'} />
              <span className={`text-[11px] font-medium ${active ? o.tone : 'text-zinc-500'}`}>{o.label}</span>
            </button>
          );
        })}
      </div>
      <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
        placeholder="Any additional feedback? (optional)" data-testid="csat-comment" />
      <div className="mt-2 flex justify-end">
        <Button size="sm" onClick={submit} disabled={submitting || !rating} data-testid="csat-submit">
          {submitting ? 'Submitting…' : 'Submit rating'}
        </Button>
      </div>
    </div>
  );
}
